import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Globe, LogOut, UserCircle, LayoutDashboard, Cloud, MonitorSmartphone, Wallet, TabletSmartphone, Share2, CreditCard, UtensilsCrossed, ShoppingBag, Apple, Scissors, Building2, Layers, Store, FileCheck, Trophy, Tag, Power, BarChart3, Briefcase, LifeBuoy, BookOpen } from 'lucide-react';
import Logo from './Logo';
import { useLanguage } from '@/lib/LanguageContext';
import { languages } from '@/lib/translations';
import { useAuth } from '@/lib/AuthContext';
import { base44 } from '@/api/base44Client';
import { useIsMaximized } from '@/hooks/useIsMaximized';
import { stripLang, localizePath } from '@/lib/langPath';

const base44Logout = () => base44.auth.logout('/');

export default function Navbar({ variant }) {
  const { lang, setLang, t } = useLanguage();
  const { user } = useAuth();
  const isMaximized = useIsMaximized();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const navigate = useNavigate();
  // La dirección puede llevar el prefijo del idioma (/en/belleza): para decidir
  // el estilo del menú y la navegación interna miramos la ruta sin ese prefijo.
  const currentPath = stripLang(useLocation().pathname);
  // variant="light" fuerza la cabecera clara (texto oscuro y logo en color) en
  // páginas que no están en el listado, como la de "no encontrado".
  const forceSolid = variant !== 'light' && !['/', '/casos-exito', '/precios', '/sobre-nosotros', '/blog', '/inteligencia-artificial', '/tpv-nube', '/cashdro', '/quioscos-autoservicio', '/hardware-tpv', '/canales-venta', '/medios-pago', '/verifactu', '/hosteleria', '/retail', '/alimentacion', '/belleza', '/cadenas-franquicias', '/contacto', '/guia-tpv', '/demo', '/tpv-barcelona', '/tpv-madrid', '/tecnicos'].includes(currentPath) && !currentPath.startsWith('/blog/') && !currentPath.startsWith('/legal/');
  const isHome = currentPath === '/';
  const isTransparent = !forceSolid && !scrolled;

  const navLinks = [
    {
      label: t('nav.solutions'),
      icon: Layers,
      children: [
        { label: t('nav.software'), href: '/tpv-nube', icon: Cloud },
        { label: t('nav.hardware'), href: '/hardware-tpv', icon: MonitorSmartphone },
        { label: t('nav.cashdro'), href: '/cashdro', icon: Wallet },
        { label: t('nav.kiosks'), href: '/quioscos-autoservicio', icon: TabletSmartphone },
        { label: t('nav.channels'), href: '/canales-venta', icon: Share2 },
        { label: t('nav.payments'), href: '/medios-pago', icon: CreditCard },
      ]
    },
    {
      label: t('nav.sectors'),
      icon: Store,
      children: [
        { label: t('nav.hospitality'), href: '/hosteleria', icon: UtensilsCrossed },
        { label: t('nav.retail'), href: '/retail', icon: ShoppingBag },
        { label: t('nav.food'), href: '/alimentacion', icon: Apple },
        { label: t('nav.beauty'), href: '/belleza', icon: Scissors },
        { label: t('nav.franchises'), href: '/cadenas-franquicias', icon: Building2 },
      ]
    },
    { label: t('nav.verifactu'), href: '/verifactu', icon: FileCheck },
    { label: t('nav.success'), href: '/casos-exito', icon: Trophy },
    { label: t('nav.pricing'), href: '/precios', icon: Tag },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Bloquear scroll del body cuando el menú móvil está abierto
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    if (!mobileOpen) setMobileSection(null);
    window.dispatchEvent(new CustomEvent('smartpos:mobile-menu', { detail: { open: mobileOpen } }));
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNav = (href, sector) => {
    setMobileOpen(false);
    setOpenDropdown(null);
    if (sector) {
      if (currentPath === '/') {
        window.dispatchEvent(new CustomEvent('setActiveSector', { detail: sector }));
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(localizePath(`/?sector=${sector}#sectores`, lang));
      }
      return;
    }
    if (href.startsWith('/')) {
      navigate(localizePath(href, lang));
      return;
    }
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(localizePath('/', lang));
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  return (
    <>
      <a href="#contenido-principal" className="skip-link">{t('nav.skipContent')}</a>
      <nav aria-label={t('nav.mainNav')} className={`fixed top-0 left-0 right-0 z-50 ${
        scrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'
      } ${forceSolid ? 'bg-brand-dark/90 backdrop-blur-xl shadow-lg shadow-blue-500/10 py-3 border-b border-white/5' : 'bg-transparent py-5'} ${mobileOpen ? 'hidden lg:block' : ''}`}>
        <div className="max-w-[90rem] mx-auto px-0 xl:px-2 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group hover:opacity-80">
            <Logo variant={isTransparent && !isHome ? 'color' : 'light'} className="h-9 lg:h-10" imgClassName="h-9 lg:h-10" invert={isTransparent && isHome} />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.label} className="relative group">
                {link.children ? (
                  <button
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${isTransparent && !isHome ? 'hover:bg-gray-100 text-gray-700 hover:text-brand-blue' : 'hover:bg-white/10 text-white/90 hover:text-white'}`}
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    {link.icon && <link.icon size={15} className="opacity-70" />}
                    {link.label}
                    <ChevronDown size={14} className="opacity-70" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleNav(link.href)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${isTransparent && !isHome ? 'hover:bg-gray-100 text-gray-700 hover:text-brand-blue' : 'hover:bg-white/10 text-white/90 hover:text-white'}`}
                  >
                    {link.icon && <link.icon size={15} className="opacity-70" />}
                    {link.label}
                  </button>
                )}
                {link.children && (
                  <div
                    className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                      openDropdown === link.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                    }`}
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <div className="bg-brand-dark/95 backdrop-blur-xl rounded-xl overflow-hidden shadow-xl shadow-blue-500/10 min-w-[180px] border border-white/10">
                      {link.children.map((child) => (
                        <button
                          key={child.label}
                          onClick={() => handleNav(child.href, child.sector)}
                          className="w-full flex items-center gap-3 text-left px-5 py-3 text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition-colors border-b border-white/10 last:border-0"
                        >
                          {child.icon && <child.icon size={16} className="text-brand-blue shrink-0" />}
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+34936302521" className={`flex items-center gap-2 text-sm font-semibold transition-colors ${isTransparent && !isHome ? 'text-gray-700 hover:text-brand-blue' : 'text-white/80 hover:text-white'}`}>
              <Phone size={14} />
              <span className="hidden xl:inline">936 302 521</span>
            </a>

            {/* Language selector */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                aria-label={t('nav.selectLanguage')}
                aria-expanded={langOpen}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${isTransparent && !isHome ? 'text-gray-700 hover:text-brand-blue hover:bg-gray-100' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
              >
                <img src={languages.find((l) => l.code === lang)?.flagUrl || `https://flagcdn.com/w40/${languages.find((l) => l.code === lang)?.flagCode}.png`} alt="" className="w-5 h-3.5 rounded-sm object-cover" loading="lazy" decoding="async" />
                <ChevronDown size={12} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                  <div className="absolute top-full right-0 mt-2 z-20 bg-brand-dark/95 backdrop-blur-xl rounded-xl overflow-hidden shadow-xl min-w-[160px] border border-white/10">
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors border-b border-white/10 last:border-0 ${
                          lang === l.code ? 'text-white bg-white/10' : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <img src={l.flagUrl || `https://flagcdn.com/w40/${l.flagCode}.png`} alt="" className="w-5 h-3.5 rounded-sm object-cover" loading="lazy" decoding="async" />
                        {l.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Login / User menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label={t('nav.userMenu')}
                  aria-expanded={userMenuOpen}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${isTransparent && !isHome ? 'text-gray-700 border-gray-300 hover:bg-gray-100' : 'text-white border-white/20 hover:bg-white/10'}`}
                >
                  <span className="w-6 h-6 rounded-full bg-brand-blue text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden xl:inline max-w-[120px] truncate">{user.full_name || user.email}</span>
                  <ChevronDown size={14} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 z-20 bg-white rounded-xl overflow-hidden shadow-xl min-w-[200px] border border-gray-100">
                      {user.role === 'admin' && (
                        <a
                          href="https://pc-builder-pro-copy-e800cabb.base44.app"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setUserMenuOpen(false)}
                          className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                        >
                          <LayoutDashboard size={16} className="text-brand-blue shrink-0" />
                          {t('nav.configurator')}
                        </a>
                      )}
                      <a
                        href="https://cloudlicense.hiopos.com/auth/#/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                      >
                        <BarChart3 size={16} className="text-brand-blue shrink-0" />
                        {t('nav.analytics')}
                      </a>
                      <a
                        href="https://cloudlicense.hiopos.com/auth/#/erp"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                      >
                        <Briefcase size={16} className="text-brand-blue shrink-0" />
                        {t('nav.hioffice')}
                      </a>
                      <a
                        href="https://smartposdevelopment.zohodesk.eu/portal/es/signin"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                      >
                        <LifeBuoy size={16} className="text-brand-blue shrink-0" />
                        {t('nav.support')}
                      </a>
                      <a
                        href="https://cloudlicense.hiopos.com/auth/#/clouddocs?customer=55211"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                      >
                        <BookOpen size={16} className="text-brand-blue shrink-0" />
                        {t('nav.clientArea')}
                      </a>
                      <button
                        onClick={() => { setUserMenuOpen(false); base44Logout(); }}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors text-left"
                      >
                        <LogOut size={16} className="text-red-500 shrink-0" />
                        {t('nav.logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${isTransparent && !isHome ? 'text-gray-700 border-gray-300 hover:bg-gray-100' : 'text-white border-white/20 hover:bg-white/10'}`}
                >
                  <UserCircle size={17} />
                  <span className="hidden xl:inline">{t('nav.clientArea')}</span>
                  <ChevronDown size={14} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute top-full right-0 mt-2 z-20 bg-white rounded-xl overflow-hidden shadow-xl min-w-[200px] border border-gray-100">
                      <a
                        href="https://cloudlicense.hiopos.com/auth/#/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                      >
                        <BarChart3 size={16} className="text-brand-blue shrink-0" />
                        {t('nav.analytics')}
                      </a>
                      <a
                        href="https://cloudlicense.hiopos.com/auth/#/erp"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                      >
                        <Briefcase size={16} className="text-brand-blue shrink-0" />
                        {t('nav.hioffice')}
                      </a>
                      <a
                        href="https://smartposdevelopment.zohodesk.eu/portal/es/signin"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 text-left"
                      >
                        <LifeBuoy size={16} className="text-brand-blue shrink-0" />
                        {t('nav.support')}
                      </a>
                      <a
                        href="https://cloudlicense.hiopos.com/auth/#/clouddocs?customer=55211"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors text-left"
                      >
                        <BookOpen size={16} className="text-brand-blue shrink-0" />
                        {t('nav.clientArea')}
                      </a>
                    </div>
                  </>
                )}
              </div>
            )}

            <button
              onClick={() => navigate(localizePath('/demo', lang))}
              className="bg-brand-blue text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#0078d4] transition-all duration-200 hover:scale-105 shadow-md shadow-blue-500/20 whitespace-nowrap"
            >
              {t('nav.demo')}
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden p-3 -mr-2 rounded-lg transition-colors ${isTransparent && !mobileOpen && !isHome ? 'text-gray-700 active:bg-gray-100' : 'text-white active:bg-white/10'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Floating pill header — appears on scroll (desktop only) */}
      <nav className={`hidden lg:flex fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-[calc(100vw-1rem)] transition-opacity duration-150 ease-out ${
        scrolled && isMaximized
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
      }`}>
        <div className="flex items-center bg-white/90 backdrop-blur-xl rounded-xl shadow-lg shadow-black/10 border border-black/5 px-1.5 py-1.5">
          {/* Power icon — vuelve a la home o sube al inicio si ya estás en ella */}
          <button
            type="button"
            onClick={() => {
              if (currentPath === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate(localizePath('/', lang));
              }
            }}
            aria-label={t('nav.home')}
            className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
          >
            <Power size={15} className="text-[#5c626d]" />
          </button>

          {/* Nav links */}
          {navLinks.map((link) => (
            <div key={link.label} className="relative group">
              {link.children ? (
                <button
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[13px] font-semibold text-gray-800 hover:bg-gray-100 transition-colors whitespace-nowrap shrink-0"
                  onMouseEnter={() => setOpenDropdown(link.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  {link.icon && <link.icon size={14} className="opacity-60 shrink-0" />}
                  {link.label}
                  <ChevronDown size={13} className="opacity-50 shrink-0" />
                </button>
              ) : (
                <button
                  onClick={() => handleNav(link.href)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[13px] font-semibold text-gray-800 hover:bg-gray-100 transition-colors whitespace-nowrap shrink-0"
                >
                  {link.icon && <link.icon size={14} className="opacity-60 shrink-0" />}
                  {link.label}
                </button>
              )}
              {link.children && (
                <div
                  className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                    openDropdown === link.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                  }`}
                  onMouseEnter={() => setOpenDropdown(link.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-xl shadow-black/10 min-w-[200px] border border-black/5">
                    {link.children.map((child) => (
                      <button
                        key={child.label}
                        onClick={() => handleNav(child.href, child.sector)}
                        className="w-full flex items-center gap-3 text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-brand-blue transition-colors border-b border-gray-100 last:border-0"
                      >
                        {child.icon && <child.icon size={16} className="text-brand-blue shrink-0" />}
                        {child.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* CTA */}
          <button
            onClick={() => navigate('/demo')}
            className="flex items-center bg-black text-white px-2.5 py-1.5 rounded-md text-[13px] font-bold hover:bg-gray-800 transition-colors ml-1 shrink-0"
          >
            {t('nav.tryFree')}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div id="mobile-menu" className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div className={`absolute top-0 right-0 h-full w-[85vw] max-w-sm bg-white shadow-2xl transition-transform duration-300 flex flex-col ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          {/* Header with close button */}
          <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100 shrink-0">
            <Logo variant="color" className="h-5 ml-3" imgClassName="h-5" />
            <button
              onClick={() => setMobileOpen(false)}
              className="w-11 h-11 -mr-2 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              aria-label="Cerrar menú"
            >
              <X size={22} />
            </button>
          </div>

          {/* Quick contact bar — always visible */}
          <div className="px-5 py-4 bg-gray-50 border-b border-gray-100 shrink-0 grid grid-cols-2 gap-3">
            <a
              href="tel:+34936302521"
              className="flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-bold text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 transition-colors"
            >
              <Phone size={15} />
              936 302 521
            </a>
            <button
              onClick={() => { setMobileOpen(false); navigate(localizePath('/demo', lang)); }}
              className="bg-brand-blue text-white h-12 rounded-xl font-bold text-sm shadow-md hover:opacity-90 transition-opacity"
            >
              {t('nav.demo')}
            </button>
          </div>

          {/* Scrollable navigation */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <div key={link.label}>
                  {link.children ? (
                    <>
                      <button
                        onClick={() => setMobileSection(mobileSection === link.label ? null : link.label)}
                        className="w-full flex items-center gap-2.5 px-3 py-3.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        {link.icon && <link.icon size={16} className="text-blue-500 shrink-0" />}
                        {link.label}
                        <ChevronDown size={16} className={`ml-auto text-gray-400 shrink-0 transition-transform ${mobileSection === link.label ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileSection === link.label && (
                        <div className="pl-4 space-y-0.5">
                          {link.children.map((child) => (
                            <button
                              key={child.label}
                              onClick={() => handleNav(child.href, child.sector)}
                              className="w-full text-left px-3 py-3 text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              {child.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleNav(link.href)}
                      className="w-full flex items-center gap-2.5 text-left px-3 py-3.5 text-sm font-semibold text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {link.icon && <link.icon size={16} className="text-blue-500 shrink-0" />}
                      {link.label}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer — language + login */}
          <div className="px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-gray-100 shrink-0 space-y-3">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-gray-400 shrink-0" />
              <div className="flex gap-1 flex-wrap">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`min-h-[44px] min-w-[44px] px-3 rounded-lg flex items-center justify-center transition-all ${
                      lang === l.code ? 'scale-110 bg-gray-100' : 'opacity-60 hover:opacity-100 active:scale-95'
                    }`}
                  >
                    <img src={l.flagUrl || `https://flagcdn.com/w40/${l.flagCode}.png`} alt={l.label} className="w-6 h-4 rounded-sm object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            </div>
            {user ? (
              <>
                <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50">
                  <span className="w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{user.full_name || 'Usuario'}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </div>
                {user.role === 'admin' && (
                  <a
                    href="https://pc-builder-pro-copy-e800cabb.base44.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                  >
                    <LayoutDashboard size={16} />
                    {t('nav.configurator')}
                  </a>
                )}
                <a
                  href="https://cloudlicense.hiopos.com/auth/#/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <BarChart3 size={16} />
                  {t('nav.analytics')}
                </a>
                <a
                  href="https://cloudlicense.hiopos.com/auth/#/erp"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <Briefcase size={16} />
                  {t('nav.hioffice')}
                </a>
                <a
                  href="https://smartposdevelopment.zohodesk.eu/portal/es/signin"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <LifeBuoy size={16} />
                  {t('nav.support')}
                </a>
                <a
                  href="https://cloudlicense.hiopos.com/auth/#/clouddocs?customer=55211"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <BookOpen size={16} />
                  {t('nav.manuals')}
                </a>
                <button
                  onClick={() => { setMobileOpen(false); base44Logout(); }}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-red-600 border border-red-200 hover:bg-red-50 active:scale-95 transition-all"
                >
                  <LogOut size={16} />
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <a
                  href="https://cloudlicense.hiopos.com/auth/#/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <BarChart3 size={16} />
                  {t('nav.analytics')}
                </a>
                <a
                  href="https://cloudlicense.hiopos.com/auth/#/erp"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <Briefcase size={16} />
                  {t('nav.hioffice')}
                </a>
                <a
                  href="https://smartposdevelopment.zohodesk.eu/portal/es/signin"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <LifeBuoy size={16} />
                  {t('nav.support')}
                </a>
                <a
                  href="https://cloudlicense.hiopos.com/auth/#/clouddocs?customer=55211"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-5 min-h-[44px] rounded-full font-bold text-sm text-brand-blue border border-brand-blue/25 bg-white hover:bg-blue-50 active:scale-95 transition-all"
                >
                  <BookOpen size={16} />
                  {t('nav.clientArea')}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}